import React, { useCallback, useEffect, useMemo } from 'react';

import { formatTableTime, localTime, Popup } from '@helpers';
import { KEYS_TAGS } from '@components/imageHandling/common';
import { useImageHandlingViewModel } from '@v2/components/imageHandling/viewmodels/useImageHandlingViewModel';
import useJobStatusBadgeConfig from '@v2/pages/planboard/hooks/useJobStatusBadgeConfig';
import { selectJobThumbnailDocument, buildJobThumbnailImageProps } from '@v2/components/FinishJobModal/helpers';
import Calendar from '@components/common/Calendar';
import type { JobInfoSectionProps } from '@v2/components/FinishJobModal/types';

export type UseManageJobInfoSectionParams = JobInfoSectionProps;

const useManageJobInfoSection = ({ item, actualEndDate, onActualEndDateChange, C, scale }: UseManageJobInfoSectionParams) => {
  const jobConfig = useJobStatusBadgeConfig({ status: item.status, C });

  const productName = item.partDescription ?? item.operationName ?? '';
  const jobNumber = item.productionOperationNumber ?? '';
  const customerName = item.customerName ?? '—';
  const scheduledEnd = formatTableTime(item.scheduledEndDate, true) || '—';
  const formattedActualEnd = formatTableTime(actualEndDate, true);

  const imageContext = useMemo(
    () => ({ scopeId: KEYS_TAGS.Operation, scopeReferenceId: item.productionOperationId }),
    [item.productionOperationId]
  );
  const {
    state: { imagesDocuments, loading: isDocumentsLoading },
    actions: { loadImagesDocuments },
  } = useImageHandlingViewModel({ imageContext });

  // Plain effect (not useFocusEffect) — the modal renders via Popup, outside NavigationContainer.
  useEffect(() => {
    loadImagesDocuments();
  }, [loadImagesDocuments]);

  const thumbnailDocument = selectJobThumbnailDocument(imagesDocuments, item.productionOperationId);

  const imageProps = useMemo(
    () => buildJobThumbnailImageProps(thumbnailDocument, item.productionOperationId, scale),
    [thumbnailDocument, item.productionOperationId, scale]
  );

  // Show a spinner while fetching so the box doesn't flash "No image" before the thumbnail resolves.
  const isImageLoading = isDocumentsLoading && !imageProps;

  const openDatePicker = useCallback(() => {
    Popup({
      Component: (p: any) => (
        <Calendar
          {...p}
          mode="single"
          selectedChartDate={actualEndDate.toISOString()}
          setSelectedChartDate={(iso: string) => {
            const current = localTime(actualEndDate);
            const merged = localTime(iso).set({
              hour: current.hour(),
              minute: current.minute(),
              second: current.second(),
              millisecond: current.millisecond(),
            });
            onActualEndDateChange(merged.toDate());
          }}
        />
      ),
    });
  }, [actualEndDate, onActualEndDateChange]);

  return {
    jobConfig,
    productName,
    jobNumber,
    customerName,
    scheduledEnd,
    formattedActualEnd,
    imageProps,
    isImageLoading,
    openDatePicker,
  };
};

export default useManageJobInfoSection;
